import React, { useState, useEffect, useCallback, Fragment } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useReturn } from "util/return";
import { useSetTitle } from "util/title";

import { profileSlice } from "redux/features/profile";
import { useLoadParticipation, participationStateContext } from "./load";

import MathContent from "util/mathjax";
import {
  Container,
  Message,
  Box,
  Columns,
  Button,
  Form,
  Element,
  Heading,
  Content,
} from "react-bulma-components";
import dayjs from "dayjs";
import deepEquals from "react-fast-compare";
import { dualSort, caseInsensitive, ascending, descending } from "util/sort";
import ActivityDates from "./dates";
import IconButton from "util/iconButton";
import { FaCheck, FaTimes, FaDollarSign } from "react-icons/fa";

import ErrorMessage from "util/errorMessage";
import { ProcessPayment } from "components/payments";
import {
  useNonPaymentRegistrationState,
  NonPaymentRegistration,
} from "components/nonPaymentRegistration";

import StructuredText from "common-frontend/util/structuredText";
import { Dollars } from "util/currency";
import { ucFirst } from "util/text";
import capacityInformation from "./capacity";

import {
  ShowForms,
  useFormState,
  FORM_STATE,
  itemForm,
} from "./registrationForms";

import {
  PromptAttendance,
  PromptTalkAttendance,
  RegistrationAttendanceItem,
  IN_PERSON,
  REMOTELY,
  isFalse,
  isSimpleTrue,
} from "./attendance";

import { RegistrationPurchaseItem } from "./purchase";

import { RegistrationProgress } from "./registrationProgress";

const { Field, Label, Control, Checkbox } = Form;

function RegistrationQuestionResponses({ participation }) {
  const items = [
    ...(participation.portal_registration_items || []),
    ...(participation.non_portal_registration_items || []),
  ];
  const activity_id = participation.activity.id;
  const questionData = items
    .filter((item) => item.activity_id === activity_id)
    .map(({ item_type, item_code }) => {
      return {
        what: item_code === "" ? "" : ` (for ${item_code} ${item_type})`,
        questionResponses:
          participation.current_registration_item_data?.[activity_id]?.[
            item_type
          ]?.[item_code]?.current_state?.question_responses,
      };
    })
    .filter(
      ({ questionResponses }) =>
        questionResponses && Object.keys(questionResponses).length > 0
    );

  if (questionData.length === 0) return null;

  return questionData.map(({ what, questionResponses }) => (
    <Fragment key={what}>
      <Label>Registration Form Question Responses{what}</Label>
      <Columns multiline>
        {Object.keys(questionResponses)
          .sort()
          .map((question, qnum) => {
            const answers = questionResponses[question].reverse();
            return (
              <Fragment key={qnum}>
                <Columns.Column size="one-third">
                  {ucFirst(question)}
                </Columns.Column>
                <Columns.Column size="two-thirds">
                  {answers.map((answerInfo, answerNumber) => (
                    <Fragment key={answerNumber}>
                      {answers.length > 1 ? (
                        <Element className="answered-when">
                          {answerNumber === 0 ? "Most recent " : "Earlier "}
                          response, as of {answerInfo.answered_when}}
                        </Element>
                      ) : null}
                      <Element>
                        {answerInfo.answer === "" ? (
                          <i>left blank</i>
                        ) : (
                          answerInfo.answer
                        )}
                      </Element>
                    </Fragment>
                  ))}
                </Columns.Column>
              </Fragment>
            );
          })}
      </Columns>
    </Fragment>
  ));
}

function PastRegistrationSummary({ participation }) {
  return (
    <Fragment>
      <Message color="info">
        <Message.Body>
          This program or event is in the past. To make corrections to
          event-specific information below, please contact a Fields Institute
          staff member. You can make changes to your demographic information,
          occupational status, or affiliation information at the{" "}
          <Link to="..">main portal dashboard</Link>
        </Message.Body>
      </Message>
      <Field>
        <Label>Registration Status</Label>
        <p>
          According to our records, you{" "}
          {participation.attending ? <b>did</b> : <b>did not</b>} attend
          {participation.attending === IN_PERSON
            ? " (in person)"
            : participation.attending === REMOTELY
            ? " (remotely)"
            : null}
          .
        </p>
      </Field>
      <RegistrationQuestionResponses {...{ participation }} />
    </Fragment>
  );
}

function UpcomingRegistrationUnsupported({ participation }) {
  return (
    <Message color="info">
      <Message.Body>
        {participation.activity.preapproval_required_for_registration ? (
          <Fragment>
            Registration for this program/event is open for preapproved
            participants only. If you have been preapproved, please sign in to
            the portal using the same e-mail address as in your approval
            notification.
          </Fragment>
        ) : participation.activity.reason_portal_registration_disabled ? (
          <Fragment>
            {participation.activity.reason_portal_registration_disabled}
          </Fragment>
        ) : (
          <Fragment>
            This program/event is not yet equipped for registration via this new
            web portal (events are gradually being added to this new system but
            this one has not yet been).
          </Fragment>
        )}{" "}
        Please refer to the{" "}
        <a href={participation.activity.public_url}>program/event web page</a>
        {null} for registration instructions.
      </Message.Body>
    </Message>
  );
}

function PerTalkRegistrationSection({ heading, talks, update, hasFuture }) {
  if (talks.length === 0) return null;
  return (
    <Box>
      <div className="block">
        <b>{heading}</b>
        {hasFuture ? (
          <div className="block">
            <i>
              For talks that are several months in the future, there is no need
              to decide now. You may leave those unselected and return to this
              form nearer the time.
            </i>
          </div>
        ) : null}
      </div>

      <Columns>
        {talks.map((t) => (
          <PromptTalkAttendance key={t.id} talk={t} update={update} />
        ))}
      </Columns>
    </Box>
  );
}

function CommonRegistrationConfirmation({ participation }) {
  const { isPast } = participation;
  const confirmationDisplays = isPast
    ? []
    : participation.portal_registration_items
        .filter(
          ({ activity_id, item_type, item_code, confirmation_display }) =>
            item_type === "attendance" &&
            activity_id === participation.activity.id &&
            participation.current_registration_item_data?.[activity_id]?.[
              item_type
            ]?.[item_code]?.current_state.attending &&
            confirmation_display
        )
        .map(({ item_code, confirmation_display }) => {
          return {
            what: item_code,
            display: confirmation_display,
          };
        });

  // FIXME: add item-specific confirmation display headers
  return (
    <Fragment>
      {confirmationDisplays.length === 0 && isPast ? null : (
        <Heading size={4}>Attendance Information</Heading>
      )}
      {confirmationDisplays.map(({ what, display }) => (
        <div className="block" key={what}>
          <StructuredText data={display} />
        </div>
      ))}
      {isPast ? null : (
        <div className="block">
          Please refer to the{" "}
          <a href={participation.activity.public_url}>program/event web page</a>
          {null} for {confirmationDisplays.length > 0 ? "aditional " : ""}
          information on attending.
        </div>
      )}
    </Fragment>
  );
}

function PerTalkRegistrationConfirmation({
  participation,
  missingAttendanceTypeFor,
  pastTalksMissingAttendance,
  upcomingTalksMissingAttendance,
}) {
  const { is_past: isPast } = participation;
  const missingItems = [];
  if (missingAttendanceTypeFor)
    missingItems.push("how you " + missingAttendanceTypeFor);
  if (pastTalksMissingAttendance)
    missingItems.push(
      "which " + (isPast ? "" : "past ") + "talks you did or did not attend"
    );

  const incomplete = missingItems.length > 0 || upcomingTalksMissingAttendance;
  return (
    <Message color="success">
      <Message.Header>
        Registration {isPast ? "Summary" : "Confirmed"}
      </Message.Header>
      <Message.Body>
        <div className="block">
          {isPast ? null : "Your registration is confirmed"}
          {incomplete && !isPast ? ", but " : null}
          {missingItems.map((item, itemNum) => (
            <Fragment key={item}>
              {itemNum > 0 ? " and " : ""}
              <span className="has-text-weight-bold has-text-danger">
                {itemNum === 0 && isPast ? "Please " : "please "}
                specify {item}
              </span>
            </Fragment>
          ))}
          {missingItems.length === 0 && upcomingTalksMissingAttendance ? (
            <span>
              you have not yet specified attendance for all upcoming talks;
              please return to this page later to confirm your attendance at
              those.
            </span>
          ) : null}
          {isPast && missingItems.length === 0 ? null : ". "}
          Your currently confirmed attendance status at each talk is{" "}
          <span className="is-underlined">underlined</span> below. To make
          changes, simply choose a different status and press the Save Changes
          button that will appear at the bottom.
        </div>
        <CommonRegistrationConfirmation participation={participation} />
      </Message.Body>
    </Message>
  );
}

function PerTalkRegistration({ participation, update, changes }) {
  const today = dayjs().format("YYYY-MM-DD");
  const earliestFirst = [caseInsensitive, ascending];
  const latestFirst = [caseInsensitive, descending];

  const talks = Object.values(participation.activity.talk_info).map((t) => {
    const items = participation.portal_registration_items || [];
    const [activityHasRemoteOption] = items
      .filter(
        ({ activity_id, item_code }) =>
          activity_id === participation.activity.id && item_code === ""
      )
      .map(({ remote_attendance_option }) => remote_attendance_option);

    const [talkHasRemoteOption] = items
      .filter(
        ({ activity_id, item_code }) =>
          activity_id === participation.activity.id &&
          item_code === "talk-" + t.id
      )
      .map(({ remote_attendance_option }) => remote_attendance_option);

    return {
      ...t,
      isPast: t.date < today,
      attending:
        changes["attending_talk_" + t.id] ??
        participation["attending_talk_" + t.id],
      origAttending: participation["attending_talk_" + t.id],
      hasRemoteOption: !!(talkHasRemoteOption ?? activityHasRemoteOption),
    };
  });

  const registered =
    participation.attending ||
    talks.filter((t) => !!t.origAttending).length > 0;

  const upcomingTalks = talks
    .filter((t) => !t.isPast)
    .sort(dualSort("date", ...earliestFirst, "time", ...earliestFirst));

  const allPast =
    participation.activity.end_date && participation.activity.end_date < today;
  const sortPastDate = allPast ? earliestFirst : latestFirst;

  const pastTalks = talks
    .filter((t) => t.isPast)
    .sort(dualSort("date", ...sortPastDate, "time", ...earliestFirst));

  const pastTalksMissingAttendance =
    pastTalks.filter((t) => !t.attending && !isFalse(t.attending)).length > 0;

  const pastTalksMissingAttendanceType =
    !pastTalksMissingAttendance &&
    pastTalks.filter((t) => t.hasRemoteOption && t.attending === true).length >
      0;

  const upcomingTalksMissingAttendanceType =
    upcomingTalks.filter((t) => t.hasRemoteOption && t.attending === true)
      .length > 0;

  const missingAttendanceTypeFor = upcomingTalksMissingAttendanceType
    ? "are attending"
    : pastTalksMissingAttendanceType
    ? "attended"
    : null;

  const upcomingTalksMissingAttendance =
    upcomingTalks.filter((t) => !t.attending && !isFalse(t.attending)).length >
    0;

  return (
    <Box>
      {talks.length === 0 ? (
        <Fragment>
          <b>Talk Registration</b>
          <Box>Talk schedule not yet available</Box>
        </Fragment>
      ) : null}
      {registered ? (
        <PerTalkRegistrationConfirmation
          {...{
            participation,
            pastTalksMissingAttendance,
            missingAttendanceTypeFor,
            upcomingTalksMissingAttendance,
          }}
        />
      ) : null}
      <PerTalkRegistrationSection
        heading="Upcoming Talks: Will You Attend?"
        talks={upcomingTalks}
        update={update}
        hasFuture
      />
      <PerTalkRegistrationSection
        heading="Past Talks: Did You Attend?"
        talks={pastTalks}
        update={update}
      />
    </Box>
  );
}

function SimpleWholeActivityRegistrationConfirmation({
  participation,
  hasRemoteOption,
  missingAttendanceType,
}) {
  return (
    <Message color="success">
      <Message.Header>Registration Confirmed</Message.Header>
      <Message.Body>
        <div className="block">
          Your registration is confirmed
          {missingAttendanceType ? (
            <Fragment>
              {" "}
              but{" "}
              <span className="has-text-weight-bold has-text-danger">
                please specify how you are attending
              </span>
            </Fragment>
          ) : null}
          . You may cancel
          {hasRemoteOption
            ? missingAttendanceType
              ? ", or tell us  whether you are attending in person or remotely,"
              : ", or switch between in-person and remote attendance,"
            : null}{" "}
          by changing your attendance option below and pressing the Save Changes
          button that will then appear.
        </div>
        <CommonRegistrationConfirmation participation={participation} />
      </Message.Body>
    </Message>
  );
}

function SimpleWholeActivityRegistration({ participation, changes, update }) {
  const alreadyRegistered = participation.attending;
  const attending = changes.attending ?? alreadyRegistered;

  const items = participation.portal_registration_items || [];
  const [mainRegistrationSetup] = items.filter(
    ({ activity_id, item_type, item_code }) =>
      activity_id === participation.activity.id &&
      item_type === "attendance" &&
      item_code === ""
  );

  const hasRemoteOption = mainRegistrationSetup?.remote_attendance_option;
  const missingAttendanceType = hasRemoteOption && isSimpleTrue(attending);
  return (
    <Fragment>
      {alreadyRegistered ? (
        <SimpleWholeActivityRegistrationConfirmation
          {...{ participation, hasRemoteOption, missingAttendanceType }}
        />
      ) : null}
      <Field>
        <Label>Are You Attending?</Label>
        <PromptAttendance
          attending={attending}
          origAttending={alreadyRegistered}
          setAttending={(v) => update("attending", v)}
          hasRemoteOption={hasRemoteOption}
          radioName="attending"
        />
      </Field>
    </Fragment>
  );
}

function buildItemKey(item) {
  return `${item.activity_id}-${item.item_type}-${item.item_code}`;
}

function ShowFormItemsForActivity({
  participation,
  formName,
  formActivityInfo,
  formInfo,
  formOpen,
  itemDetails,
  setItemDetails,
}) {
  const {
    display_heading,
    display_body_structured_text,
    activity_id,
    activity_description,
    items,
  } = formActivityInfo;

  const sectionDisplay =
    formName === "registration" && formOpen[formName]
      ? { display_heading, display_body_structured_text }
      : {};

  const setOneItemDetails = (item, details) => {
    const key = buildItemKey(item);
    setItemDetails((prev) => {
      const updated = { ...prev };
      updated[key] = details;
      return updated;
    });
  };

  // Render each item either inside a message box (when form is open for
  // editing) or outside (when form is closed or open for cancellation, in
  // which case the render function normally renders nothing but just adjusts
  // currentDetails as needed). This means it will be unmounted and remounted
  // so cannot track its own state, so we tell it the current and previous
  // state.

  // As well as telling the item the previous form state, also tell it the
  // previous participation and item data, so if we learn about a change on
  // the server while the form is open, the item will see it.

  // We use state values to hold each of these "previous" pieces of information.
  // An effect fires to set the previous information to the current information
  // if different. This means that, when the information changes, there will
  // be one render (before the effect fires) when the item can see that there
  // is a difference between current and previous information, and will use
  // this fact to re-initialize its current item details using the correct
  // current information.

  const openFor = formInfo.formState.registration.openFor;
  const [previouslyOpenFor, setPreviouslyOpenFor] = useState(openFor);
  const [previousParticipationData, setPreviousParticipationData] = useState(
    participation
  );

  const currentItemsByKey = Object.fromEntries(
    items.map((item) => {
      const key = `${item.activity_id}-${item.item_type}-${item.item_code}`;
      return [key, item];
    })
  );

  const [previousItemsByKey, setPreviousItemsByKey] = useState(
    currentItemsByKey
  );

  useEffect(() => {
    if (openFor !== previouslyOpenFor) setPreviouslyOpenFor(openFor);
  }, [openFor, previouslyOpenFor]);

  useEffect(() => {
    if (!deepEquals(currentItemsByKey, previousItemsByKey))
      setPreviousItemsByKey(currentItemsByKey);
  }, [currentItemsByKey, previousItemsByKey]);

  useEffect(() => {
    if (!deepEquals(participation, previousParticipationData))
      setPreviousParticipationData(participation);
  }, [participation, previousParticipationData]);

  // If the person has launched a form to "register" or "update registration
  // for" (but not to "cancel") an activity, and the activity's attendance
  // is not at capacity, we can assume the person IS going to be attending
  // the activity -- so, no need to ask any "Are you attending?" question,
  // just "How Are You Attending?" if remote/in person information desired.

  const [primaryActivityItem] = items.filter(
    (item) =>
      item.item_type === "attendance" &&
      item.item_code === "" &&
      item.activity_id === participation.activity.id
  );

  const canAssumeAttendingPrimaryActivity =
    openFor !== FORM_STATE.CANCEL &&
    primaryActivityItem &&
    capacityInformation(primaryActivityItem).available.toMe > 0;

  // Also, in this case, if no remote/in-person information is desired,
  // ont only can we assume the person is attending for the purpose of
  // confguring the main attendance component, we can go ahead and display
  // other items that depend on attendance, without waiting for attendance
  // to be explicirlt set.

  const canAssumeAttendanceForOtherPrimaryActivityItems =
    canAssumeAttendingPrimaryActivity &&
    !primaryActivityItem?.remote_attendance_option;

  const displayedItems = items.map((item) => {
    const key = buildItemKey(item);
    const currentDetails = itemDetails[key];

    const isPrimaryActivity = item.activity_id === participation.activity.id;
    const activityAttendanceKey = `${item.activity_id}-attendance-`;

    const canAssumeAttendingActivity =
      isPrimaryActivity &&
      (key === activityAttendanceKey
        ? canAssumeAttendingPrimaryActivity
        : canAssumeAttendanceForOtherPrimaryActivityItems);

    const attendingActivity =
      itemDetails[activityAttendanceKey]?.attending ||
      canAssumeAttendingActivity;

    const wasAttendingActivity =
      participation.current_registration_item_data?.[item.activity_id]
        ?.attendance?.[""]?.item_values?.attending;

    // If there is any type of item for this activity that will generate
    // a section heading (that is, any type of purchase, or any type of
    // attendance other than just overall attendance or program visit
    // attendance), signal this to each component, so the main attendance
    // component can also print a heading to distinguish itself from the
    // other items.
    const showMainAttendanceHeading =
      items.filter(
        ({ activity_id, item_type, item_code }) =>
          !(
            item_type === "attendance" &&
            (item_code === "" || item_code === "program_itself")
          )
      ).length > 0;

    return (
      <Fragment key={key}>
        {attendingActivity && item.display_heading ? (
          // Display item heading if there is one, but only if the activity
          // the item is for is being attended. Otherwise the item typically
          // won't be displayed at all (Registration_____Item component below
          // will return null and is only rendered for its side effects) so
          // don't display heading in that case.
          <Heading renderAs="h2" size={4}>
            {item.display_heading}
          </Heading>
        ) : null}

        {attendingActivity && item.display_body_structured_text ? (
          // Ditto for display_body
          <div className="block">
            <StructuredText data={item.display_body_structured_text} />
          </div>
        ) : null}

        {item.item_type === "attendance" ? (
          <RegistrationAttendanceItem
            key={key}
            itemKey={key}
            {...{
              item,
              participation,
              currentDetails,
              openFor,
              previouslyOpenFor,
              showMainAttendanceHeading,
            }}
            previousItem={previousItemsByKey[key]}
            {...{
              previousParticipationData,
              attendingActivity,
              wasAttendingActivity,
              canAssumeAttendingPrimaryActivity,
            }}
            setDetails={(details) => setOneItemDetails(item, details)}
          />
        ) : item.item_type === "purchase" ? (
          <RegistrationPurchaseItem
            key={key}
            itemKey={key}
            {...{
              item,
              participation,
              currentDetails,
              openFor,
              previouslyOpenFor,
            }}
            attending={attendingActivity}
            wasAttending={wasAttendingActivity}
            previousItem={previousItemsByKey[key]}
            previousParticipationData={previousParticipationData}
            setDetails={(details) => setOneItemDetails(item, details)}
          />
        ) : (
          <ErrorMessage key={key}>
            Internal error: Unsupported registration type. Please contact
            Institute staff for assistance. Our apologies!
          </ErrorMessage>
        )}
      </Fragment>
    );
  });

  return (
    <Fragment>
      {sectionDisplay.display_heading ? (
        <Heading>{sectionDisplay.display_heading}</Heading>
      ) : null}
      {sectionDisplay.display_body_structured_text ? (
        <div className="block">
          <StructuredText data={sectionDisplay.display_body_structured_text} />
        </div>
      ) : null}

      {formName === "registration" &&
      activity_description &&
      formOpen[formName] ? (
        <Message key={"registration-" + activity_id} color="primary">
          <Message.Header>{activity_description}</Message.Header>
          <Message.Body>{displayedItems}</Message.Body>
        </Message>
      ) : (
        displayedItems
      )}
    </Fragment>
  );
}

function ShowItemsForForm({
  participation,
  form,
  formInfo,
  formOpen,
  itemDetails,
  setItemDetails,
}) {
  const { formName, activities } = form;
  return activities.map((formActivityInfo) => (
    <ShowFormItemsForActivity
      key={formActivityInfo.activity_id}
      {...{
        participation,
        formName,
        formActivityInfo,
        formInfo,
        formOpen,
        itemDetails,
        setItemDetails,
      }}
    />
  ));
}

function EmbeddedRedirect({
  participation,
  embeddedIn,
  registeredThroughEmbedding,
  hasPortalRegistration,
}) {
  return (
    <Message color="warning">
      <Message.Header>
        This Event is Part of{" "}
        {embeddedIn
          .map((embedding) => "the " + embedding.short_title)
          .join(" and ")}
      </Message.Header>
      <Message.Body>
        {registeredThroughEmbedding ? (
          <Fragment>
            You are therefore already considered to be registered, since you are
            registered for{" "}
            {embeddedIn
              .filter(({ attending }) => attending)
              .map((embedding) => "the " + embedding.short_title.join(" and "))}
          </Fragment>
        ) : (
          <Fragment>
            <Content>
              {hasPortalRegistration
                ? "If you are attending any other lectures, please"
                : "Please"}{" "}
              go to the full registration form:
              {embeddedIn.map(({ id, title }) => (
                <p key={id}>
                  <Link to={"../participation/" + id}>{title}</Link>
                </p>
              ))}
              {hasPortalRegistration ? (
                <p>
                  If you are registering <b>only</b> for the{" "}
                  {participation.activity.short_title}, then you may do so
                  below.
                </p>
              ) : null}
            </Content>
          </Fragment>
        )}
      </Message.Body>
    </Message>
  );
}

function FullWholeActivityRegistration({
  personId,
  participation,
  setPaymentItemsInProgress,
  startNonPaymentRegistration,
  doClose,
  receiptEmail,
}) {
  const { portal_registration_items } = participation;

  const formInfo = useFormState(participation);
  const formOpen = Object.fromEntries(
    formInfo.formNameList.map((form) => [
      form,
      formInfo.formState[form].openFor !== FORM_STATE.CLOSED &&
        formInfo.formState[form].openFor !== FORM_STATE.CANCEL,
    ])
  );
  const someFormsOpen = Object.values(formOpen).includes(true);
  const [itemDetails, setItemDetails] = useState({});

  const alteredForms = portal_registration_items
    .filter((item) => {
      const details = itemDetails[buildItemKey(item)];
      return (
        details && details.changed && !details.incomplete && !details.hasError
      );
    })
    .map((item) => itemForm(item).formName);

  const itemsInAlteredForms = portal_registration_items.filter(
    (item) =>
      itemDetails[buildItemKey(item)] &&
      alteredForms.includes(itemForm(item).formName)
  );

  const incompleteMessages = portal_registration_items
    .map((item) => {
      const details = itemDetails[buildItemKey(item)];
      return details?.incomplete;
    })
    .filter((incomplete) => !!incomplete);
  const incomplete = incompleteMessages.length > 0;

  const hasError = portal_registration_items
    .map((item) => {
      const details = itemDetails[buildItemKey(item)];
      return details?.hasError;
    })
    .includes(true);

  // Items to pass on to the payment page (if payment required) or to
  // send directly to the non-payment-registration API. The format is the
  // same for both. Note that we include fee_selection data and unit_cost
  // in both cases; the non-payment-registration API will remove them if
  // it is an item for which payment never applies.

  // We include all items in any "form" that has been altered, so, for example,
  // registration-type items are included even if only one attendance is
  // unaltered; this is necessary for thematic program registrations where
  // only a couple of events have been changed, so that it still counts as
  // registration "for the program" rather than multi-event registration
  // for two unrelatedevents.

  const finalItems = itemsInAlteredForms.map((item) => {
    const details = itemDetails[buildItemKey(item)];

    const finalPayload = {
      activity_id: item.activity_id,
      item_type: item.item_type,
      item_code: item.item_code,
      total_amount_override: details.total_amount_override,
      unit_cost: details.unit_cost,
      quantity: details.quantity,
      fee_selection_data: details.fee_selection_data,
      question_responses: details.question_responses,
      waitlist_desired: details.waitlist_desired,
    };
    if (item.item_type === "attendance") {
      finalPayload.attending = details.attending;
      finalPayload.attendance_dates = details.attendance_dates;
    }
    return finalPayload;
  });

  const paymentRequired = finalItems
    .filter(
      (item) =>
        item.total_amount_override !== 0 &&
        item.unit_cost > 0 &&
        (item.quantity ?? 1) > 0
    )
    .reduce(
      (partialSum, item) =>
        partialSum +
        (item.total_amount_override ?? item.unit_cost * (item.quantity ?? 1)),
      0
    );

  const changed = finalItems.length > 0;
  const dispatch = useDispatch();
  const launchPaymentPage = () => {
    if (incomplete) return;
    if (!paymentRequired) return;
    dispatch(profileSlice.actions.setRegistrationPending(null));
    setPaymentItemsInProgress(finalItems);
  };

  const submitNonPaymentRegistration = () => {
    dispatch(profileSlice.actions.setRegistrationPending(null));
    startNonPaymentRegistration({
      items: finalItems,
      receipt_requested: true,
    });
  };

  const forms = [];
  const findOrAppend = (list, field, value, template = {}) => {
    const [found] = list.filter((entry) => entry[field] === value);
    if (found) return found;
    const newEntry = { [field]: value, ...template };
    list.push(newEntry);
    return newEntry;
  };

  participation.portal_registration_items.forEach((item) => {
    const { activity_id, activity_description, item_type, item_code } = item;
    const formName = itemForm(item).formName;
    const form = findOrAppend(forms, "formName", formName, { activities: [] });
    const formActivityInfo = findOrAppend(
      form.activities,
      "activity_id",
      activity_id,
      {
        activity_description,
        items: [],
      }
    );

    // Any display_heading / display_body_structured_text in the main
    // attendance item for an activity are taken out and put into the
    // overall formActivityInfo structure, for display at the top of the
    // registration form.  Ones that are present in a subsidiary item
    // are left in the item, to generate an optional subheading or
    // instructional display above a specific item.

    const itemToUse = { ...item };
    if (item_type === "attendance" && item_code === "") {
      ["display_heading", "display_body_structured_text"].forEach((field) => {
        formActivityInfo[field] = item[field];
        itemToUse[field] = null;
      });
    }
    formActivityInfo.items.push(itemToUse);
  });

  const programForms = participation.activity.program_registrations;
  const haveProgramForms = programForms.length > 0;
  const multipleProgramForms = programForms.length > 1;
  const [thisEventOnly, setThisEventOnly] = useState(false);

  const thematicRedirect = haveProgramForms ? (
    <Message color="warning">
      <Message.Header>
        This Event is Part of a Thematic or Focus Program
      </Message.Header>
      <Message.Body>
        Unless you are{" "}
        <b>
          <i>only registering for this one event</i>
        </b>
        , please go to {multipleProgramForms ? "one of " : ""}
        the following program registration form
        {multipleProgramForms ? "s" : ""}:
        <Content>
          {programForms.map(({ id, title }) => (
            <p key={id}>
              <Link to={"../participation/" + id}>{title}</Link>
            </p>
          ))}
        </Content>
        <Field>
          <Control>
            <Checkbox
              checked={thisEventOnly}
              onChange={(e) => setThisEventOnly(e.target.checked)}
            >
              {" "}
              Open registration form for this event only
            </Checkbox>
          </Control>
        </Field>
      </Message.Body>
    </Message>
  ) : null;

  if (haveProgramForms && !thisEventOnly) return thematicRedirect;

  return (
    <Fragment>
      {thematicRedirect}
      <ShowForms {...formInfo} />
      {forms.map((form) => (
        <ShowItemsForForm
          key={form.formName}
          {...{
            participation,
            form,
            formInfo,
            formOpen,
            itemDetails,
            setItemDetails,
          }}
        />
      ))}

      {incomplete && !hasError ? (
        <Fragment>
          {incompleteMessages.map((msg, i) => (
            <ErrorMessage key={i}>{msg}</ErrorMessage>
          ))}
          <IconButton icon={FaTimes} onClick={doClose}>
            Discard Changes and Close
          </IconButton>
        </Fragment>
      ) : paymentRequired ? (
        <Fragment>
          <Heading size={4}>Payment</Heading>
          <Message color="success">
            <Message.Body>
              <Content>
                <p>
                  Total amount due: <b>{Dollars.format(paymentRequired)}.</b>
                </p>
                <Message color="danger">
                  <Message.Body>
                    If you believe this amount is in error, please contact the
                    appropriate staff member listed on the event's web page, and
                    do not proceed further.
                  </Message.Body>
                </Message>
                <p>
                  Payment must be made by credit card, or by a VISA Debit or
                  Debit Mastercard (other debit cards are not supported).
                  <b>
                    <i>Once you have your card at hand and are ready to pay,</i>{" "}
                  </b>
                  please press "Pay {Dollars.format(paymentRequired)} Now" to be
                  taken to the credit card payment page.
                </p>
                <p>
                  Once you are in the payment page, please do not close your
                  browser window or use the back button. You need to either
                  complete payment or press the "Back" or "Cancel" button to
                  return here. If you do neither, the pending payment will
                  eventually be terminated automatically and you will receive an
                  e-mail notification informing you of the incomplete payment.
                </p>
              </Content>
            </Message.Body>
          </Message>
          {/* FIXME: add means of posting a credit card processing service
           status message into the database, and display this message when
           there is onw.
          <Message color="danger">
            <Message.Header>Service Disruption</Message.Header>
            <Message.Body>
              <Content>
              The payment processing company is currently ...
              </Content>
            </Message.Body>
          </Message>
         */}
          <div className="buttons">
            <IconButton icon={FaDollarSign} onClick={launchPaymentPage}>
              Pay {Dollars.format(paymentRequired)} Now
            </IconButton>
            <IconButton icon={FaTimes} onClick={doClose}>
              Cancel
            </IconButton>
          </div>
        </Fragment>
      ) : changed ? (
        <div className="buttons">
          <IconButton icon={FaCheck} onClick={submitNonPaymentRegistration}>
            {formInfo.submitText}
          </IconButton>
          <IconButton icon={FaTimes} onClick={doClose}>
            Discard Changes and Close
          </IconButton>
        </div>
      ) : someFormsOpen && !hasError ? (
        <Message color="warning">
          <Message.Body>
            <i>Information unchanged</i>
          </Message.Body>
        </Message>
      ) : null}
      {paymentRequired || changed ? (
        <Content>
          {receiptEmail ? (
            <Fragment>
              A receipt will be sent to your <i>preferred contact address</i>{" "}
              which is currently set to <b>{receiptEmail}</b> and can be changed{" "}
            </Fragment>
          ) : (
            <Fragment>
              To receive a receipt by e-mail, first select a preferred contact
              address{" "}
            </Fragment>
          )}
          <Link to="../basic/emails">here</Link>.
        </Content>
      ) : null}
    </Fragment>
  );
}

function RedirectForProfileReview({ activity, itemStatus }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    dispatch(
      profileSlice.actions.setRegistrationPending({
        id: activity.id,
        title: activity.title,
        require_demographics: activity.require_demographics,
      })
    );
    navigate("../", { replace: true });
  }, [
    activity.id,
    activity.title,
    activity.require_demographics,
    dispatch,
    navigate,
  ]);
  return null;
}

// For use when rendering <FullWholeActivityRegistration>: per-item breakdown
// of any changes between the previous and current participation data, along
// with the formula for computing a registration item's key string, assembled
// here so we can add them to the participationStateContext to be accessible
// to every component within the FullWholeActivityRegistration tree.

function perItemParticipationState(participationState) {
  const buildItemKey = (item) =>
    ["activity_id", "item_type", "item_code"].map((k) => item[k]).join("-");

  const allData = {
    current: participationState.loadedData || { portal_registration_items: [] },
    previous: participationState.previouslyLoadedData || {
      portal_registration_items: [],
    },
  };

  const itemIndependentData = Object.fromEntries(
    Object.keys(allData).map((when) => [
      when,
      { ...allData[when], portal_registration_items: null },
    ])
  );

  const itemData = Object.fromEntries(
    Object.keys(allData).map((when) => [
      when,
      Object.fromEntries(
        allData[when].portal_registration_items.map((item) => [
          buildItemKey(item),
          { ...item },
        ])
      ),
    ])
  );

  const changed = {
    baseData: !deepEquals(
      itemIndependentData.current,
      itemIndependentData.previous
    ),
    itemBaseData: {},
    itemFeeData: {},
  };

  Object.entries(itemData.current).forEach(([itemKey, item]) => {
    const prevItem = itemData.previous[itemKey] || { ask_for: [] };
    const currentParams = Object.fromEntries(
      item.ask_for.map((ask) => [ask.key_string, ask.previous_submission])
    );
    const previousParams = Object.fromEntries(
      prevItem.ask_for.map((ask) => [ask.key_string, ask.previous_submission])
    );
    const changedParams = !deepEquals(currentParams, previousParams);
    const itemBase = changedParams ? { ...item, fee_categories: null } : item;
    const prevBase = changedParams
      ? { ...prevItem, fee_categories: null }
      : prevItem;
    changed.itemBaseData[itemKey] = !deepEquals(itemBase, prevBase);
    changed.itemFeeData[itemKey] = changedParams;
  });

  return {
    ...participationState,
    buildItemKey,
    changed,
  };
}

function EditParticipation() {
  useSetTitle("Program/Event Registration");
  const {
    id: personId,
    data: personData,
    itemStatus,
    registrationPending,
  } = useSelector(profileSlice.selector);
  const { activityId } = useParams();

  const { apiState, participationState } = useLoadParticipation(
    personId,
    activityId
  );

  const { loadState, saveState, update, clear, save, changeRecord } = apiState;

  const participation = participationState.loadedData || {};
  const changes = changeRecord ? changeRecord.changes : {};
  const doReturn = useReturn();
  const dispatch = useDispatch();

  const doClose = useCallback(() => {
    dispatch(profileSlice.actions.setRegistrationPending(null));
    doReturn();
  }, [dispatch, doReturn]);

  const doCancel = () => {
    clear();
    doClose();
  };

  const [closeAfterSaving, setCloseAfterSaving] = useState(false);
  useEffect(() => {
    if (closeAfterSaving && saveState.failed) setCloseAfterSaving(false);
    if (closeAfterSaving && !changeRecord) {
      doClose();
      setCloseAfterSaving(false);
    }
  }, [closeAfterSaving, saveState.failed, changeRecord, doClose]);

  const doSave = () => {
    dispatch(profileSlice.actions.setRegistrationPending(null));
    if (changeRecord) {
      update("receipt_requested", receiptRequested);
      save();
    }
    // setCloseAfterSaving(true);
  };

  const closeButton = (
    <div className="buttons">
      <IconButton icon={FaTimes} onClick={doClose}>
        Close
      </IconButton>
    </div>
  );
  const [receiptRequested, setReceiptRequested] = useState(true);

  const [paymentItemsInProgress, setPaymentItemsInProgress] = useState(null);

  const nonPaymentRegistrationState = useNonPaymentRegistrationState(personId);

  const startNonPaymentRegistration = nonPaymentRegistrationState.start;
  if (paymentItemsInProgress)
    return (
      <ProcessPayment
        onFinished={() => setPaymentItemsInProgress(null)}
        personId={personId}
        items={paymentItemsInProgress}
      />
    );

  if (nonPaymentRegistrationState.inProgress)
    return (
      <NonPaymentRegistration
        state={nonPaymentRegistrationState}
        personId={personId}
      />
    );

  if (loadState.error)
    return (
      <Container className="spaced">
        <Message color="danger">
          <Message.Header>
            Error loading information <Button remove onClick={doClose} />
          </Message.Header>
          <Message.Body>
            {loadState.error.message}
            {closeButton}
          </Message.Body>
        </Message>
      </Container>
    );
  if (loadState.pending && !apiState.data) return "Loading data; please wait.";

  if (!participation.activity) {
    return (
      <Container className="spaced">
        <Message color="danger">
          <Message.Header>
            Program/Event Not Found <Button remove onClick={doClose} />
          </Message.Header>
          <Message.Body>
            No such program/event found.
            {closeButton}
          </Message.Body>
        </Message>
      </Container>
    );
  }

  if (
    (participation.attending === null ||
      participation.attending === undefined) &&
    (!itemStatus.affiliation.reviewed || !itemStatus.demographics.reviewed)
  )
    return (
      <RedirectForProfileReview
        activity={participation.activity}
        itemStatus={itemStatus}
      />
    );

  const hasPortalRegistration =
    participation.activity.portal_registration_enabled;

  const usesSimpleFreeRegistration =
    hasPortalRegistration &&
    participation.portal_registration_items.length === 1 &&
    participation.portal_registration_items.filter(
      (item) =>
        item.unavailable ||
        item.fee_categories !== null ||
        item.questions.length > 0 ||
        item.item_type !== "attendance" ||
        item.item_code !== "" ||
        item.activity_id !== participation.activity.id ||
        item.display_heading !== null ||
        item.display_body_structured_text !== null
    ).length === 0;

  const embeddedIn = participation.activity.embedded_in ?? [];
  const registeredThroughEmbedding =
    embeddedIn.filter(({ attending }) => attending).length > 0;

  const showPerTalkRegistration =
    hasPortalRegistration &&
    participation.activity.talk_info &&
    !registeredThroughEmbedding;
  const showPast =
    participation.is_past &&
    !showPerTalkRegistration &&
    !registeredThroughEmbedding;
  const showUnsupported =
    !showPast && !hasPortalRegistration && embeddedIn.length === 0;
  const showPortalActivityRegistration =
    !showPast &&
    hasPortalRegistration &&
    !showPerTalkRegistration &&
    !registeredThroughEmbedding;
  const showSimpleActivityRegistration =
    showPortalActivityRegistration && usesSimpleFreeRegistration;
  const showFullActivityRegistration =
    showPortalActivityRegistration && !usesSimpleFreeRegistration;

  const receiptEmail = personData.primary_email_address?.address;

  const saveOrCancelButtons = (
    <Fragment>
      <div className="buttons">
        <IconButton icon={FaCheck} onClick={doSave}>
          Save Changes
        </IconButton>
        <IconButton icon={FaTimes} onClick={doCancel}>
          Cancel Changes
        </IconButton>
      </div>
      {showSimpleActivityRegistration ? (
        <Fragment>
          {receiptEmail ? (
            <Field>
              <Control>
                <Checkbox
                  checked={receiptRequested}
                  onChange={(e) => setReceiptRequested(e.target.checked)}
                  disabled={Object.keys(changes).length === 0}
                >
                  {" "}
                  Send me a receipt by e-mail
                </Checkbox>
              </Control>
            </Field>
          ) : null}
          <p>
            {receiptEmail ? (
              <Fragment>
                Receipt will be sent to your <i>preferred contact address</i>{" "}
                which is currently set to <b>{receiptEmail}</b> and can be
                changed{" "}
              </Fragment>
            ) : (
              <Fragment>
                To receive a receipt by e-mail, first select a preferred contact
                address{" "}
              </Fragment>
            )}
            <Link to="../basic/emails">here</Link>.
          </p>
        </Fragment>
      ) : null}
    </Fragment>
  );

  return (
    <Container className="spaced">
      {registrationPending ? (
        <RegistrationProgress forActivity={participation.activity} />
      ) : null}
      <Message>
        <Message.Header>
          <ActivityDates activity={participation.activity} />
          <MathContent>
            {participation.activity.title}
            {loadState.pending || saveState.pending ? (
              <Control loading />
            ) : null}
          </MathContent>
          <Button remove onClick={doClose} />
        </Message.Header>
        <Message.Body>
          {saveState.failed ? (
            <Message color="danger">
              <Message.Header>Failed to save changes</Message.Header>
              <Message.Body>
                {saveState.failed.message ?? "An unknown error occurred"}
              </Message.Body>
            </Message>
          ) : null}

          {embeddedIn.length > 0 ? (
            <EmbeddedRedirect
              {...{
                participation,
                embeddedIn,
                hasPortalRegistration,
                registeredThroughEmbedding,
              }}
            />
          ) : null}

          {showPast ? (
            <PastRegistrationSummary participation={participation} />
          ) : null}

          {showUnsupported ? (
            <UpcomingRegistrationUnsupported participation={participation} />
          ) : null}

          {showPerTalkRegistration ? (
            <PerTalkRegistration {...{ participation, update, changes }} />
          ) : null}

          {showSimpleActivityRegistration ? (
            <SimpleWholeActivityRegistration
              {...{ participation, update, changes }}
            />
          ) : null}

          {showFullActivityRegistration ? (
            <participationStateContext.Provider
              value={perItemParticipationState(participationState)}
            >
              <FullWholeActivityRegistration
                {...{
                  participation,
                  personId,
                  doClose,
                  setPaymentItemsInProgress,
                  startNonPaymentRegistration,
                  receiptEmail,
                }}
              />
            </participationStateContext.Provider>
          ) : null}

          {saveState.pending
            ? "Saving changes; please wait"
            : showFullActivityRegistration
            ? null
            : changeRecord
            ? saveOrCancelButtons
            : closeButton}
        </Message.Body>
      </Message>
    </Container>
  );
}

export default EditParticipation;
